// The figures of every book, for NoVA: what exists, where each is printed,
// where the web edition shows it, and the pitch beats that take a room to
// one. Data comes from bookFigures.gen.js (scripts/generate-figure-index.mjs
// reads the masters and the rendered PDFs); the onboarding guidebook's
// entries are hand-authored in onboardingGuide.js and merged here by id.
//
// Pure data + string builders — copied verbatim into frontend/src/data by
// generate-entities.mjs, so no Node imports.
import { BOOK_FIGURES } from './bookFigures.gen.js';
import { GUIDE_FIGURES } from './onboardingGuide.js';

export { BOOK_FIGURES };

const BOOK_LINES = {
  platform: { title: 'The platform book', line: 'The platform book is the engineering edition: how nventr.io and an App Kit instance are built, module by module, with a figure for every seam — topology, tenancy, the gate, the runtime, the fleet.', answers: ['the platform book', 'platform book', 'engineering book', 'technical documentation', 'the technical docs', 'architecture diagrams'] },
  protocols: { title: 'License and deployment protocols', line: 'The protocols book is how a customer deliverable ships: four targets, two buckets, one gate and one ledger — the hard floor beneath every delivery and the sealed flow for object-code customers.', answers: ['the protocols book', 'protocols', 'delivery protocols', 'license protocols', 'how deliveries ship', 'sealed delivery'] },
  routing: { title: 'Smart model routing', line: 'The routing book is how the Dev Console picks a model: signals in, a tier out, and every outcome back into the ledger so the next call starts lower and climbs only on a failure the runtime can check.', answers: ['the routing book', 'model routing', 'smart routing', 'how models are chosen', 'routing loop', 'tiers'] },
  brain: { title: 'The project brain', line: 'The brain book is how the project remembers: a compiled wiki of systems, decisions, lessons and runbooks that every agent reads before it works and writes after.', answers: ['the brain book', 'project brain', 'second brain'] },
  appkit: { title: 'The App Kit', line: 'The App Kit book is the rented-kit model: the stack, the IP boundary between kit and layer, deployment targets, licensing and usage reporting.', answers: ['the app kit book', 'app kit documentation', 'rented kit'] },
};

const guideIds = new Set(GUIDE_FIGURES.map((f) => f.id));
const head = (c) => c.split(' — ')[0].trim();
const words = (s) => s.toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/).filter((w) => w.length > 2 && !STOP.has(w));
const STOP = new Set(['the', 'and', 'one', 'two', 'four', 'how', 'what', 'with', 'into', 'from', 'that', 'this', 'for', 'its', 'are', 'not', 'any', 'all', 'per', 'each', 'every', 'same', 'glance']);
const clamp = (s, n = 48) => { const w = s.split(/\s+/); return w.length <= n ? s : `${w.slice(0, n).join(' ')}…`; };

// The words a person would use for a figure: the caption head and its
// comma/colon pieces (minus a leading brand token), the file-name words,
// the section title — each at least two words so a bare noun cannot hijack.
function figureAnswers(f) {
  const h = head(f.caption).toLowerCase();
  const pieces = h.split(/\s*[:,;—–]\s*/).map((x) => x.replace(/^(nventr\.io|nventr|the)\s+/, '').trim());
  const all = [h, ...pieces, words(f.file.replace(/\.[a-z]+$/, '')).join(' '), f.sectionTitle.toLowerCase()];
  return [...new Set(all.filter((a) => a.length > 3 && a.includes(' ')))];
}

// Figures of the books other than the guidebook (whose beats are built in
// pitch.js from GUIDE_FIGURES), grouped per book, in document order.
export function docsFigures() {
  return BOOK_FIGURES.filter((f) => !guideIds.has(f.id) && f.route === '/docs');
}

// Pitch beats: one node per book (`walk: true` — focused on it, "next"
// runs figure by figure), one child per figure. A figure's line is its
// caption; its answers are the caption head, the file-name words and the
// section title — the words a person reading the printed book would use.
export function bookPitchNodes() {
  const byBook = new Map();
  for (const f of docsFigures()) { if (!byBook.has(f.book)) byBook.set(f.book, []); byBook.get(f.book).push(f); }
  const nodes = [];
  for (const [key, figs] of byBook) {
    const meta = BOOK_LINES[key] || { title: figs[0].bookTitle, line: `${figs[0].bookTitle}: ${figs.length} figures.`, answers: [figs[0].bookTitle.toLowerCase()] };
    nodes.push({
      id: `${key}-book`, title: meta.title, route: '/docs', section: `${key}-book`, walk: true, gated: true,
      line: clamp(meta.line), answers: meta.answers,
      children: figs.map((f) => ({
        id: f.id, title: head(f.caption), route: '/docs', section: f.id, page: f.page, gated: true,
        line: clamp(`${f.caption}.`),
        answers: figureAnswers(f),
      })),
    });
  }
  return nodes;
}

// The block NoVA reads while the page is /docs: every figure on screen by
// id, with its printed page and caption head — and the rule that a request
// for a picture is a pitch_step, never "I can't show that".
export function bookFiguresText() {
  const figs = docsFigures();
  const books = [...new Set(figs.map((f) => f.book))];
  const lines = books.map((b) => {
    const fs = figs.filter((f) => f.book === b);
    return `${(BOOK_LINES[b] || {}).title || fs[0].bookTitle} (${fs.length}): ${fs.map((f) => `${f.id}${f.page ? ` (print p.${f.page})` : ''} — ${head(f.caption)}`).join('; ')}`;
  });
  return `THE BOOKS ON SCREEN (/docs). ${figs.length} figures are on this page as <figure id> elements, in the sections of their books; the printed editions carry the same pictures on the pages given. ${lines.join(' · ')}. A request for a picture, diagram, chart or "the figure on page N of the X book" → pitch_step with the figure id or the person's words; the page scrolls to it and \`say\` tells you what it shows. Never say you cannot show or render a figure before you have tried pitch_step.`;
}

// Every figure NoVA may show, as one list: the guidebook's (public, on
// /guide) and the books' (members, on /docs). `src` is the image the dock
// can embed in the transcript as ![caption](src).
export function allFigures({ member = false } = {}) {
  const guide = GUIDE_FIGURES.map((f) => ({
    id: f.id, book: 'onboarding', bookTitle: 'The onboarding guidebook', src: `/guide/${f.file}`, caption: f.caption,
    page: f.page, route: '/guide', section: f.id, answers: f.answers || [],
  }));
  if (!member) return guide;
  return [...guide, ...docsFigures().map((f) => ({ id: f.id, book: f.book, bookTitle: f.bookTitle, src: f.src, caption: f.caption, page: f.page, route: f.route, section: f.id, answers: figureAnswers(f) }))];
}

// The figures that match what someone said — by id, printed page ("page
// 13"), or words in the caption / answers / section title. Ranked, best
// first; empty when nothing fits. Pure, so the dock could run it too.
export function findFigures(text, { member = false, limit = 5 } = {}) {
  const t = String(text || '').toLowerCase().trim();
  if (!t) return [];
  const pageAsk = t.match(/\b(?:page|p\.)\s*(\d{1,3})\b/);
  const tw = words(t);
  const scored = allFigures({ member }).map((f) => {
    let s = 0;
    if (t.includes(f.id)) s += 10;
    if (pageAsk && Number(pageAsk[1]) === f.page) s += 6;
    for (const a of f.answers) if (a && t.includes(a)) s += 4;
    const iw = new Set(words(f.id.replace(/-/g, ' ')));
    const fw = new Set(words(`${f.caption} ${f.bookTitle}`));
    for (const w of tw) s += iw.has(w) ? 2 : fw.has(w) ? 1 : 0;
    return { f, s };
  }).filter((x) => x.s >= 2).sort((a, b) => b.s - a.s);
  return scored.slice(0, limit).map(({ f }) => f);
}

// Printed page by figure file name (all books) — the docs sync stamps it on captions.
export function figurePageByFile() {
  return new Map(BOOK_FIGURES.map((f) => [f.file, f.page]));
}
